<template>
  <div>
    <verify-address
      v-if="verifyAddress"
      :default-address="defaultAddress"
      :default-city="defaultCity"
      :default-state="defaultState"
      :staff-override="showStaffTools"
    />
    <div class="cart" v-if="showCart">
      <div v-click-outside="closeCart">
        <cart-header
          @close-cart="closeCart"
          :showSpinner="cartIsLoading"
          :showStaffCartInfo="showStaffTools"
          :showPhone="cart.showPhone"
          :firstNameShow="cart.firstNameShow"
          :lastNameShow="cart.lastNameShow"
        />

        <div class="cart-body">
          <div v-if="cart.showChooseZone" class="zone-information">
            Delivery Zone:
            <a href="/selectZone.pl?goloc=chooseRestaurant.pl">{{ cart.chooseZone }}</a>
          </div>
          <cart-orders
            v-for="order in cart.orders"
            :key="order.id"
            :order="order"
            :showStaffCartInfo="showStaffTools"
            @edit-item="handleEditItem"
            @remove-item="handleRemove"
            @clear-order="handleClearOrder"
            @edit-item-qty="handleEditItemQty"
            @updateRestComment="handleUpdateRestComment"
            @close-cart="closeCart"
          />

          <cart-fee-summary
            v-if="showStaffTools"
            :subtotal="cart.subtotal"
            :taxService="taxService"
            :delivery="cart.delivery"
            :couponAdj="couponAdjustment"
            :gratuity="cart.gratuityAmount"
            :minFee="cart.difference"
            :custIsPayingMinFee="cart.custIsPayingMinFee"
            :utensilsPrice="cart.utensilsPrice"
            :utensilsApplied="cart.utensilsApplied"
            :taxAmount="cart.taxAmount"
            :serviceFee="cart.serviceFee"
          />

          <cart-gratuity-picker
            v-if="showStaffTools"
            :options="cart.gratuityOptions"
            :message="cart.gratuityMessage"
            :gratuityPercent="cart.gratuityPercent"
            :gratuityAmount="cart.gratuityAmount"
            @gratuity-change="setGratuity"
          />

          <div v-if="tipErrors.minimum && showStaffTools" class="min-status-text" tabindex="0" :aria-label="tipErrors.minimum">
            <i class="fas fa-exclamation-circle alert-icon"></i>&nbsp;
            <span>{{ tipErrors.minimum }}</span>
          </div>

          <cart-coupon
            ref="coupon"
            v-if="showStaffTools"
            :value="cart.couponName"
            :isCouponApplied="isCouponApplied"
            @input="handleCouponNameChange"
            @remove-coupon="handleRemoveCoupon"
            @apply-coupon="handleApplyCoupon"
          />

          <cart-total v-if="showStaffTools" :total="cart.total" />

          <div
            v-if="cart.orders.length && (!cart.minMetOrOverridden || cart.minimumStatus === 4)"
            class="min-status-text"
            tabindex="0"
            :aria-label="minStatusText"
          >
            <i class="fas fa-exclamation-circle alert-icon"></i>&nbsp;
            {{ minStatusText }}
          </div>
          <div v-if="showPayTheDifference" class="min-status-text">
            <label v-if="cart.custIsPayingMinFee" for="paydifference" class="visuallyHidden">Click to remove minimum fee.</label>
            <label v-else for="paydifference" class="visuallyHidden">Click to pay the minimum fee.</label>
            <input id="paydifference" type="checkbox" :checked="cart.custIsPayingMinFee" @click="submitPayMinFee()" />&nbsp; (Just pay the
            difference)
          </div>

          <div v-if="!cart.orders.length" class="empty-cart-container">
            <icon-cart-outline />
            <p>Your Cart is empty! Add items to get started.</p>
          </div>

          <div
            v-if="!showStaffTools && cart.orders.length"
            class="taxes-fees-message"
            tabindex="0"
            aria-label="Taxes & Fees calculated at checkout."
          >
            <i class="fas fa-exclamation-circle alert-icon"></i>&nbsp; Taxes & Fees calculated at checkout.
          </div>

          <cart-button v-if="cart.showSendToQueue" @submit-cart="sendToQueue">Send Back To Queue </cart-button>

          <cart-button
            v-if="cart.orders.length && !cart.showSendToQueue"
            @submit-cart="submit"
            :disabled="!cart.minMetOrOverridden"
            :aria-label="`Go To Checkout ${getAmountLabel}`"
            >{{ getAmountShow }}</cart-button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vClickOutside from 'v-click-outside'

import toCurrency from './filters/toCurrency'

import CartHeader from './Cart/CartHeader.vue'
import CartOrders from './Cart/CartOrders.vue'
import CartFeeSummary from './Cart/CartFeeSummary.vue'
import CartGratuityPicker from './Cart/CartGratuityPicker.vue'
import CartCoupon from './Cart/CartCoupon.vue'
import CartTotal from './Cart/CartTotal.vue'
import CartButton from './Cart/CartButton.vue'
import VerifyAddress from './VerifyAddressModal.vue'
import IconCartOutline from './icons/IconCartOutline.vue'

import { mapState, mapActions } from 'vuex'

export default {
  name: 'CustomerCart',
  components: {
    CartHeader,
    CartOrders,
    CartFeeSummary,
    CartGratuityPicker,
    CartCoupon,
    CartTotal,
    CartButton,
    VerifyAddress,
    IconCartOutline,
  },
  mounted() {
    this.setInitCustomerCartState()
    var self = this
    var cartToggle = document.getElementById('cart_total_show')
    cartToggle.addEventListener('click', async function () {
      if (!self.showCart) {
        self.updateShowCart(true)
        await self.getCustomerCartData()
        if (document.getElementById('close-cart')) {
          self.$nextTick(function () {
            document.getElementById('close-cart').focus()
          })
        }
        return false
      }
      self.updateShowCart(false)
    })
    cartToggle.addEventListener('keyup', async function (event) {
      if (event.code === 'Enter' || event.code === 'Space') {
        event.preventDefault()
        if (!self.showCart) {
          self.updateShowCart(true)
          await self.getCustomerCartData()
          self.$nextTick(function () {
            if (document.getElementById('close-cart')) {
              document.getElementById('close-cart').focus()
            }
          })
          return false
        }
        self.updateShowCart(false)
      }
      return false
    })
    var cartCountToggle = document.getElementById('cartCountButton')
    cartCountToggle.addEventListener('click', async function () {
      if (!self.showCart) {
        self.updateShowCart(true)
        await self.getCustomerCartData()
        if (document.getElementById('close-cart')) {
          self.$nextTick(function () {
            document.getElementById('close-cart').focus()
          })
        }
        return false
      }
      self.updateShowCart(false)
    })
  },
  directives: {
    clickOutside: vClickOutside.directive,
  },
  filters: {
    toCurrency,
  },
  props: {
    showVerifyAddress: String,
    defaultAddress: String,
    defaultCity: String,
    defaultState: String,
  },
  data() {
    return {
      gratuityAmount: 0,
      couponCode: '',
      verifyAddress: false,
      tipErrors: {},
    }
  },
  computed: {
    ...mapState({
      cart: (state) => state.restMenu.customerCart,
      cartIsLoading: (state) => state.restMenu.cartIsLoading,
      showCart: (state) => state.restMenu.showCart,
      showStaffTools: (state) => state.restMenu.restInfo.showStaffTools,
    }),
    getAmountShow() {
      var amountShow = this.showStaffTools ? this.cart.total : this.cart.subtotal
      if (!this.cart.orders.length) {
        return 'Cart Empty'
      } else if (this.cart.minimumStatus === 4) {
        amountShow = parseFloat(amountShow) + parseFloat(this.cart.difference)
      }
      return `Go To Checkout ${toCurrency(amountShow, false)}`
    },
    getAmountLabel() {
      if (this.showStaffTools) {
        return `Go To Checkout, Total: $${this.cart.total}`
      } else if (this.cart.orders.length) {
        return `Go To Checkout, Subtotal: $${this.cart.subtotal}`
      } else {
        return 'Cart Empty'
      }
    },
    gratuity() {
      var gratuity = parseFloat(this.cart.gratuityPercent) * parseFloat(this.cart.subtotal)
      return gratuity
    },
    taxService() {
      var taxService = parseFloat(this.cart.taxAmount) + parseFloat(this.cart.serviceFee)
      return taxService
    },
    couponAdjustment() {
      var couponAdj = 0
      if (this.cart.couponAdj) {
        if (this.cart.couponAdj.slice(0, 1) === '-') {
          couponAdj = parseFloat(this.cart.couponAdj.slice(1))
        } else {
          couponAdj = parseFloat(this.cart.couponAdj)
        }
      }
      return couponAdj
    },
    isCouponApplied() {
      if (this.cart.couponName) {
        return true
      }
      return false
    },
    minStatusText() {
      var status = this.cart.minimumStatus
      switch (status) {
        case 1:
          return 'Add $' + this.cart.difference + ' more food to meet the minimum and enable check out.'
          break
        case 2:
          return '$' + this.cart.minimumOrder + ' food minimum required to enable check out.'
          break
        case 4:
          return 'You are paying $' + this.cart.difference + ' to meet the food minimum.'
        default:
          return ''
      }
    },
    showPayTheDifference() {
      var status = this.cart.minimumStatus
      if (!this.cart.orders.length) {
        return false
      }
      if (status !== 0 && status !== 3) {
        return true
      }
      return false
    },
  },
  methods: {
    ...mapActions({
      setInitCustomerCartState: 'restMenu/setInitCustomerCartState',
      updateShowCart: 'restMenu/updateShowCart',
      getCustomerCartData: 'restMenu/getCustomerCartData',
      removeItem: 'restMenu/removeItem',
      editItemQty: 'restMenu/editItemQty',
      setTip: 'restMenu/setTip',
      removeCoupon: 'restMenu/removeCoupon',
      applyCoupon: 'restMenu/applyCoupon',
      sendBackToQueue: 'restMenu/sendBackToQueue',
      payMinFee: 'restMenu/payMinFee',
      updateMinMetOrOverridden: 'restMenu/updateMinMetOrOverridden',
      restComment: 'restMenu/restComment',
      editItem: 'restMenu/editItem',
      clearOrder: 'restMenu/clearOrder',
    }),
    async setGratuity(payload) {
      this.tipErrors = {}
      if (payload.value === '-1') {
        var tipResponse = await this.setTip({
          mode: 'custom',
          tipAmt: payload.customTipAmount,
        })
        if (tipResponse.errors) {
          this.tipErrors = tipResponse.errors
        }
        return false
      } else {
        var tipResponse = await this.setTip({
          mode: 'perc',
          tipPerc: payload.value,
        })
        if (tipResponse.errors) {
          this.tipErrors = tipResponse.errors
        }
      }
      this.gratuityAmount = payload.value
    },
    closeCart() {
      this.updateShowCart(false)
    },
    handleUpdateRestComment(payload) {
      this.restComment({
        restId: payload.restId,
        comment: payload.comment,
      })
    },
    handleEditItem(payload) {
      this.editItem({ itemId: payload.itemId, restId: payload.restId })
    },
    handleClearOrder(restID) {
      this.clearOrder(restID)
    },
    async handleRemove(payload) {
      await this.removeItem({
        itemId: payload.id,
        restId: payload.restId,
      })
      return false
    },
    async handleEditItemQty(payload) {
      var optionIds = []
      payload.options.forEach((option) => {
        optionIds.push(option.optionid)
      })
      await this.editItemQty({
        itemId: payload.id,
        restId: payload.restId,
        qty: payload.qty,
        options: optionIds,
        whofor: payload.whofor,
        comment: payload.comment,
      })
      return false
    },
    handleCouponNameChange(input) {
      this.couponCode = input
    },
    async handleRemoveCoupon() {
      await this.removeCoupon()
      await this.getCustomerCartData()
    },
    async handleApplyCoupon(couponCode) {
      await this.applyCoupon({ couponCode: couponCode })
    },
    submit() {
      if (this.showVerifyAddress === '1') {
        this.closeCart()
        this.verifyAddress = true
        return false
      }
      window.location.href = '/checkout.pl'
    },
    sendToQueue() {
      this.sendBackToQueue({
        cartId: this.cart.cartId,
      })

      return false
    },
    submitPayMinFee() {
      const IN_QUEUE = this.cart.inQueue === 1 ? true : false
      if (document.getElementById('paydifference').checked == true) {
        if (IN_QUEUE) {
          this.payMinFee({
            remove: '',
            cartId: this.cart.cartId,
          })
        } else {
          this.payMinFee({ remove: '' })
        }
      } else {
        if (IN_QUEUE) {
          this.payMinFee({
            remove: 'dontpay',
            cartId: this.cart.cartId,
          })
        } else {
          this.payMinFee({ remove: 'dontpay' })
        }
      }
    },
  },
}
</script>

<style>
.cartBackground {
  background-color: #000;
  opacity: 0.5;
  position: fixed;
  top: 0;
  height: 100%;
  width: 100%;
  z-index: 100;
}
.zone-information {
  margin-bottom: 10px !important;
}
.zone-information a {
  text-decoration: underline;
}
.min-status-text,
.taxes-fees-message {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px !important;
}
.visuallyHidden {
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
}
.empty-cart-container {
  height: 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  font-size: 16px !important;
}
</style>

<style lang="scss" scoped>
.cart {
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  max-width: 470px;
  z-index: 502;
  max-height: 100vh;
  overflow: auto;
  max-height: -webkit-fill-available;
  @media (max-width: $mobileLandscapeMax) {
    top: 3%;
    max-width: 100vw;
  }
  @media (max-width: $mobilePortraitMax) {
    max-height: 50vh;
  }
}

.cart-body {
  background-color: $white;
  padding: 20px 15px;
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;
  box-shadow: 0px 10px 30px -17px $black;
}

.taxes-fees-message {
  font-size: 1.4rem;
  font-weight: 700;
  color: $royal;
  align-items: baseline;
}
</style>
